import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { FileIcon, FolderIcon, ListView } from "@carbon6/c6-frontend-design";
import { formatBytes } from "@/shared/utils/format/bytes";
import { breadcrumbOf, type SourceFile } from "../../domain/sourceFile";
import { resolveDatasetErrorMessage } from "../../feature/datasetFormatters";
import { SourceFileListSource } from "../SourceFileListSource";
import styles from "./SourceFileSection.module.css";

type SourceFileSectionProps = {
  datasetUuid: string;
  /** 목록에 보일 데이터셋 이름 — root 빵부스러기의 첫 칸이 된다. */
  datasetName: string;
};

/**
 * 원본 파일 목록 — 폴더 한 칸씩 스토어를 그때그때 읽는다.
 *
 * 트리 전체를 들고 있지 않다. 서버가 한 칸을 한 요청으로 주므로 화면도 지금 폴더만 알고,
 * 목록은 c6 `ListView` 가 창(페이지) 단위로 이어 받는다 — 바닥에 닿기 전에 다음 창을 미리
 * 당기고 행은 가상화한다. 수십만 개짜리 prefix 에서도 첫 화면이 같은 속도다.
 *
 * 포맷을 가리지 않는다. Misc 원본은 이 목록이 전부이고, LeRobot 은 여기에 Probe · 점검이
 * 더 붙는다.
 */
export function SourceFileSection({ datasetName, datasetUuid }: SourceFileSectionProps) {
  const [parent, setParent] = useState("");
  // 커서는 폴더의 것이라 폴더를 옮기면 소스를 새로 만든다.
  const source = useMemo(
    () =>
      new SourceFileListSource({
        datasetUuid,
        parent,
        resolveErrorMessage: resolveDatasetErrorMessage,
      }),
    [datasetUuid, parent],
  );
  useEffect(() => {
    void source.initialize();
    return () => source.dispose();
  }, [source]);
  const snapshot = useSyncExternalStore(source.subscribe, source.getSnapshot);

  const trail = breadcrumbOf(parent);

  return (
    <div className={styles.section}>
      <nav aria-label="원본 폴더 경로" className={styles.trail}>
        <button
          className={styles.crumb}
          disabled={parent === ""}
          type="button"
          onClick={() => setParent("")}
        >
          {datasetName}
        </button>
        {trail.map((crumb, index) => (
          <span key={crumb.path} className={styles.trail}>
            <span aria-hidden className={styles.separator}>
              /
            </span>
            <button
              className={styles.crumb}
              disabled={index === trail.length - 1}
              type="button"
              onClick={() => setParent(crumb.path)}
            >
              {crumb.name}
            </button>
          </span>
        ))}
      </nav>
      <ListView<SourceFile>
        ariaLabel="원본 파일"
        className={styles.list}
        emptyMessage="이 폴더에는 파일이 없습니다."
        errorMessage={snapshot.errorMessage}
        getKey={(entry) => entry.path}
        hasMore={snapshot.hasMore}
        items={snapshot.items}
        loadingMore={snapshot.loadingMore}
        renderItem={(entry) => <SourceFileRow entry={entry} />}
        retryLabel="다시 시도"
        selectedKey={null}
        status={snapshot.status}
        variant="compact"
        virtualization={{}}
        onLoadMore={() => void source.loadMore()}
        onRetry={() => void source.refresh()}
        // 방향키는 탐색만 한다. 클릭·Enter·Space로 확정한 폴더만 연다.
        onSelect={(entry, _index, { reason }) => {
          if (entry.isDir && reason === "activation") {
            setParent(entry.path);
          }
        }}
      />
    </div>
  );
}

function SourceFileRow({ entry }: { entry: SourceFile }) {
  return (
    <span className={styles.row} data-kind={entry.isDir ? "dir" : "file"}>
      <span aria-hidden className={styles.icon}>
        {entry.isDir ? <FolderIcon /> : <FileIcon />}
      </span>
      <span className={styles.name}>{entry.name}</span>
      {/* 폴더는 크기를 그리지 않는다 — 0 과 "없음" 을 같게 보이지 않게. */}
      <span className={styles.size}>{entry.isDir ? "" : formatBytes(entry.sizeBytes)}</span>
    </span>
  );
}
